import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Loader } from '@progress/kendo-react-indicators';
import { CreatingField } from '../../../components/CreatingField';
import { fetchApi } from '../../../services/api';
import { JsonResponse } from 'TypeGen/json-response';
import { SHOP_GROUP_ID } from './WorkOrderDetail';

type Props = {
  workOrderId: number;
  onClose: () => void;
  onTaskCreated: () => void;
};

type ApplicationUser = {
  ID: string;
  Name: string;
};

type UsersDropdownResponse = {
  Users: ApplicationUser[];
  SelfID: string;
};

type FormState = {
  Description: string;
  AssignedTo: string | null;
  EstimatedHours: number | null;
  LaborRate: number;
  Note: string;
};

const defaultFormState: FormState = {
  Description: '',
  AssignedTo: null,
  EstimatedHours: null,
  LaborRate: 0,
  Note: ''
};

const AddWorkOrderTaskDialog: React.FC<Props> = ({ workOrderId, onClose, onTaskCreated }) => {
  const [form, setForm] = useState<FormState>({ ...defaultFormState });
  const [users, setUsers] = useState<ApplicationUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoadingUsers(true);
    fetchApi(`/api/User/UsersDropdown?groupId=${SHOP_GROUP_ID}`)
      .then((response: UsersDropdownResponse) => {
        setUsers(response.Users);
        if (response.SelfID) {
          setForm((prev) => ({ ...prev, AssignedTo: response.SelfID }));
        }
      })
      .catch((err) => {
        console.error('Failed to load users dropdown.', err);
        setError('Unable to load user list.');
      })
      .finally(() => setLoadingUsers(false));
  }, []);

  const resetAndClose = () => {
    setForm({ ...defaultFormState });
    setError(null);
    setSaving(false);
    onClose();
  };

  const canSave = useMemo(() => {
    return form.Description.trim().length > 0 && !saving;
  }, [form.Description, saving]);

  const handleSave = async () => {
    if (!canSave) {
      setError('Please complete the required fields.');
      return;
    }

    setSaving(true);
    setError(null);

    const payload = {
      WorkOrderID: workOrderId,
      Description: form.Description.trim(),
      AssignedTo: form.AssignedTo ? form.AssignedTo : null,
      EstimatedHours: form.EstimatedHours ?? null,
      LaborRate: form.LaborRate ?? 0,
      Note: form.Note.trim().length ? form.Note.trim() : null
    };

    try {
      const response = await fetchApi('/api/ShopWorkOrder/CreateWorkOrderTask', payload, 'POST') as JsonResponse;
      if (!response?.Success) {
        setError(response?.ErrorMessage || 'Unable to add task.');
        setSaving(false);
        return;
      }
      setForm({ ...defaultFormState });
      setSaving(false);
      onTaskCreated();
    } catch (err) {
      console.error('Failed to create work order task.', err);
      setError('Unable to add task.');
      setSaving(false);
    }
  };

  return (
    <Dialog title="Add Work Order Task" onClose={resetAndClose} className="dialog-w800">
      <div className="k-form k-form-md">
        {(loadingUsers || saving) && (
          <div className="mb-3 d-flex justify-content-center">
            <Loader type="converging-spinner" />
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="mb-3">
          <label className="form-label fw-bold">Concern</label>
          <CreatingField
            data={form.Description}
            required
            renderEditable={(value, onChange) => (
              <textarea
                className="form-control"
                maxLength={2048}
                rows={3}
                disabled={saving}
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
            onChange={(value) => setForm((prev) => ({ ...prev, Description: value }))}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Assign To</label>
          <CreatingField
            data={form.AssignedTo}
            renderEditable={(value, onChange) => (
              <DropDownList
                data={users}
                textField="Name"
                dataItemKey="ID"
                defaultItem={{ ID: '', Name: 'Select User' }}
                value={value ? users.find((user) => user.ID === value) ?? null : null}
                onChange={(e) => {
                  const id = e.target.value?.ID;
                  onChange(id && id.length ? id : null);
                }}
                disabled={saving || loadingUsers}
                style={{ width: '100%' }}
              />
            )}
            onChange={(value) => setForm((prev) => ({ ...prev, AssignedTo: value }))}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Estimated Hours</label>
          <CreatingField
            dataType="number"
            data={form.EstimatedHours}
            inputProps={{ format: 'n1', min: 0, step: 0.5, placeholder: 'Optional', disabled: saving }}
            onChange={(value) => setForm((prev) => ({ ...prev, EstimatedHours: value }))}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Labor Rate</label>
          <CreatingField
            dataType="number"
            data={form.LaborRate}
            inputProps={{ format: 'c2', min: 0, step: 5, disabled: saving }}
            onChange={(value) => setForm((prev) => ({ ...prev, LaborRate: value ?? 0 }))}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Notes</label>
          <CreatingField
            data={form.Note}
            renderEditable={(value, onChange) => (
              <textarea
                className="form-control"
                rows={3}
                maxLength={2048}
                disabled={saving}
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
            onChange={(value) => setForm((prev) => ({ ...prev, Note: value }))}
          />
        </div>

      </div>
      <DialogActionsBar>
        <Button type="button" onClick={resetAndClose} disabled={saving}>
          Cancel
        </Button>
        <Button type="button" themeColor="primary" disabled={!canSave} onClick={handleSave}>
          Save Task
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default AddWorkOrderTaskDialog;
